import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export type CategoryType = 'income' | 'expense' | 'transfer';

export interface PersonalCategory {
  id: string;
  user_id: string;
  name: string;
  icon: string | null;
  color: string | null;
  category_type: CategoryType;
  parent_id: string | null;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  children?: PersonalCategory[];
}

export interface CreateCategoryInput {
  name: string;
  icon?: string;
  color?: string;
  category_type: CategoryType;
  parent_id?: string;
}

export interface UpdateCategoryInput {
  name?: string;
  icon?: string;
  color?: string;
  parent_id?: string | null;
  is_active?: boolean;
}

interface UsePersonalCategoriesOptions {
  categoryType?: CategoryType;
  includeInactive?: boolean;
}

export function usePersonalCategories(options?: UsePersonalCategoriesOptions) {
  const { user, session, initialized } = useAuth();
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams();
  if (options?.categoryType) queryParams.set('type', options.categoryType);
  if (options?.includeInactive) queryParams.set('includeInactive', 'true');
  const queryString = queryParams.toString();
  const endpoint = `/personal/categories${queryString ? `?${queryString}` : ''}`;

  const {
    data: categories,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'personalCategories',
      user?.id,
      options?.categoryType,
      options?.includeInactive,
    ],
    queryFn: async (): Promise<PersonalCategory[]> => {
      if (!session?.access_token) {
        return [];
      }

      const response = await api.get<{ categories: PersonalCategory[] }>(
        endpoint,
        session.access_token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch categories');
      }

      return response.data?.categories ?? [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
    enabled: initialized && !!user && !!session?.access_token,
  });

  // Flatten hierarchy for cases where a flat list is needed
  const flatCategories =
    categories?.reduce<PersonalCategory[]>((acc, cat) => {
      acc.push(cat);
      if (cat.children) {
        acc.push(...cat.children);
      }
      return acc;
    }, []) ?? [];

  // Convenience filters by type
  const incomeCategories =
    categories?.filter((c) => c.category_type === 'income') ?? [];
  const expenseCategories =
    categories?.filter((c) => c.category_type === 'expense') ?? [];
  const transferCategories =
    categories?.filter((c) => c.category_type === 'transfer') ?? [];

  const invalidateCategories = () =>
    queryClient.invalidateQueries({ queryKey: ['personalCategories'] });

  const createCategory = useMutation({
    mutationFn: async (input: CreateCategoryInput) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.post<{ category: PersonalCategory }>(
        '/personal/categories',
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to create category');
      return response.data!.category;
    },
    onSuccess: invalidateCategories,
  });

  const updateCategory = useMutation({
    mutationFn: async ({ id, ...input }: UpdateCategoryInput & { id: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.patch<{ category: PersonalCategory }>(
        `/personal/categories/${id}`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to update category');
      return response.data!.category;
    },
    onSuccess: invalidateCategories,
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.delete<{ message: string }>(
        `/personal/categories/${id}`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to delete category');
      return response.data!;
    },
    onSuccess: invalidateCategories,
  });

  return {
    categories: categories ?? [],
    flatCategories,
    incomeCategories,
    expenseCategories,
    transferCategories,
    isLoading,
    error,
    refetch,
    createCategory,
    updateCategory,
    deleteCategory,
  };
}
